import React from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  GraduationCap,
  BrainCircuit,
  BadgeCheck,
  CheckCircle2,
} from "lucide-react";
import AnimatedHeading from "../../../animations/AnimatedHeading";

const requirementIcons = [ShieldCheck, GraduationCap, BrainCircuit, BadgeCheck];

const RoleDetails = ({ job }) => {
  if (!job) return null;

  return (
    <section className="relative overflow-hidden py-6 sm:py-8 lg:py-6 font-saira">
      {/* Background Glow */}
      <motion.div
        animate={{
          x: [25, -25, 25],
          y: [-15, 15, -15],
        }}
        transition={{
          duration: 14,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-orange-500/10 blur-[140px]"
      />

      <motion.div
        animate={{
          x: [-25, 25, -25],
          y: [15, -15, 15],
        }}
        transition={{
          duration: 16,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-orange-500/10 blur-[140px]"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/10 px-4 py-2">
            <Sparkles size={16} className="text-orange-500" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-orange-400 sm:text-sm">
              ROLE DETAILS
            </span>
          </div>

          <div>
            <AnimatedHeading
              title="Everything You Need To Know"
              highlight="Need To Know"
            />
          </div>

          <p
            className="mx-auto max-w-7xl text-base leading-8 sm:text-lg sm:leading-9"
            style={{ color: "var(--text-secondary)" }}
          >
            Discover the qualifications we're looking for and the
            responsibilities you'll take on. Everything you need to understand
            this opportunity is presented below in one place, helping you
            explore the role, expectations, and the impact you'll make as part
            of our team.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          {/* Requirements */}
          {job?.requirements?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border transition-all duration-300 hover:border-orange-500/40"
              style={{
                backgroundColor: "var(--card-bg)",
                borderColor: "var(--border-primary)",
              }}
            >
              <div className="absolute left-0 top-0 h-full w-1 bg-orange-500" />

              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#f9731620,transparent_70%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

              <div className="relative z-10 flex h-full flex-col">
                <div
                  className="border-b px-6 py-5"
                  style={{ borderColor: "var(--border-primary)" }}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10 text-orange-500">
                      <ShieldCheck size={24} />
                    </div>

                    <div>
                      <span className="block text-xs font-semibold uppercase tracking-[0.22em] text-orange-400">
                        REQUIREMENTS
                      </span>

                      <h3
                        className="mt-1 text-xl font-bold"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Key Requirements
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="flex-1 px-6 py-5">
                  <div className="space-y-4">
                    {job.requirements.map((item, index) => {
                      const Icon =
                        requirementIcons[index % requirementIcons.length];

                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-start gap-4 rounded-xl p-3 transition-all duration-300"
                          whileHover={{
                            backgroundColor: "var(--bg-tertiary)",
                          }}
                        >
                          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-orange-500">
                            <Icon size={18} />
                          </div>

                          <p
                            className="flex-1 text-[15px] leading-7"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {item}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Responsibilities */}
          {job?.responsibilities?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border transition-all duration-300 hover:border-orange-500/40"
              style={{
                backgroundColor: "var(--card-bg)",
                borderColor: "var(--border-primary)",
              }}
            >
              <div className="absolute left-0 top-0 h-full w-1 bg-orange-500" />

              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#f9731620,transparent_70%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

              <div className="relative z-10 flex h-full flex-col">
                <div
                  className="border-b px-6 py-5"
                  style={{ borderColor: "var(--border-primary)" }}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10 text-orange-500">
                      <CheckCircle2 size={24} />
                    </div>

                    <div>
                      <span className="block text-xs font-semibold uppercase tracking-[0.22em] text-orange-400">
                        RESPONSIBILITIES
                      </span>

                      <h3
                        className="mt-1 text-xl font-bold"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Key Responsibilities
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="flex-1 px-6 py-5">
                  <div className="space-y-4">
                    {job.responsibilities.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-start gap-4 rounded-xl p-3 transition-all duration-300"
                        whileHover={{
                          backgroundColor: "var(--bg-tertiary)",
                        }}
                      >
                        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-orange-500">
                          <CheckCircle2 size={18} />
                        </div>

                        <p
                          className="flex-1 text-[15px] leading-7"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          {item}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RoleDetails;